/**
 * Canvas 2D rendering: the arena on the left, a scoreboard side panel on the
 * right (ported in spirit from `SDLAfficheScores` / `SDLAfficheInterface`).
 * The neon look comes from `shadowBlur`.
 */

import type { GridBounds } from "../sim/collision";
import type { Match, Player } from "../sim/match";
import { BonusKind, COLOR_HEX, PlayerState } from "../sim/types";
import { PANEL_WIDTH } from "../game/config";
import type { Bonus } from "../sim/bonus";
import type { TrailTracker } from "./trail-tracker";
import type { ParticleField } from "./particles";
import { drawScanlines } from "./effects";

/** Trail width in px — the bike's short side (constant; see trail-tracker.ts). */
const TRAIL_WIDTH = 5;

const BONUS_STYLE: Record<Bonus["kind"], { fill: string; label: string }> = {
  [BonusKind.Clean]: { fill: "#ffffff", label: "C" },
  [BonusKind.Boost]: { fill: "#33ccff", label: "B" },
  [BonusKind.Jump]: { fill: "#7bed4b", label: "J" },
};

export interface RenderOverlay {
  /** Seconds left on the pre-round countdown; renders a big number. */
  countdown?: number;
  /** Dim the arena (used behind the round-over / game-over screens). */
  dim?: boolean;
  /** Screen-shake pixel offset for the arena. */
  shakeX?: number;
  shakeY?: number;
  /** Draw CRT scanlines + vignette over everything. */
  crt?: boolean;
  /** Explosion shards, drawn in arena space. */
  particles?: ParticleField;
}

export class CanvasRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly bloomCanvas: HTMLCanvasElement;
  private readonly bloomCtx: CanvasRenderingContext2D;
  readonly width: number;
  readonly height: number;
  /** X where the scoreboard side panel starts (in canvas pixels). */
  readonly arenaWidth: number;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly grid: GridBounds,
  ) {
    this.canvas = canvas;
    this.arenaWidth = grid.x * 2 + grid.width;
    this.width = this.arenaWidth + PANEL_WIDTH;
    this.height = grid.y * 2 + grid.height;
    canvas.width = this.width;
    canvas.height = this.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;

    this.bloomCanvas = document.createElement("canvas");
    this.bloomCanvas.width = Math.round(this.width / 2);
    this.bloomCanvas.height = Math.round(this.height / 2);
    const bctx = this.bloomCanvas.getContext("2d");
    if (!bctx) throw new Error("Canvas 2D unavailable");
    this.bloomCtx = bctx;
  }

  /** `alpha` in [0,1]: fraction of the tick elapsed, to interpolate bikes. */
  render(
    match: Match | null,
    trails: TrailTracker,
    alpha: number,
    overlay: RenderOverlay = {},
  ): void {
    const { ctx } = this;
    ctx.fillStyle = "#02040a";
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.save();
    ctx.translate(overlay.shakeX ?? 0, overlay.shakeY ?? 0);
    this.drawGrid();
    if (match) {
      this.drawTrails(match, trails, alpha);
      this.drawBonuses(match.bonuses);
      this.drawBikes(match, alpha);
    }
    overlay.particles?.draw(ctx);
    ctx.restore();

    this.bloom();

    if (match && overlay.dim) {
      ctx.fillStyle = "rgba(2, 4, 10, 0.55)";
      ctx.fillRect(0, 0, this.arenaWidth, this.height);
    }
    this.drawPanel(match);
    if (overlay.countdown !== undefined) this.drawCountdown(overlay.countdown);
    if (overlay.crt) drawScanlines(ctx, this.width, this.height);
  }

  /** Cheap bloom: downscale the frame, blur it, add it back over the top. */
  private bloom(): void {
    const { ctx, bloomCanvas: bc, bloomCtx: bctx } = this;
    bctx.clearRect(0, 0, bc.width, bc.height);
    bctx.drawImage(this.canvas, 0, 0, bc.width, bc.height);
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = 0.5;
    ctx.filter = "blur(2px)";
    ctx.drawImage(bc, 0, 0, this.width, this.height);
    ctx.restore();
  }

  private drawTrails(match: Match, trails: TrailTracker, alpha: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.lineWidth = TRAIL_WIDTH;
    ctx.lineJoin = "miter";
    ctx.lineCap = "square";

    for (const [index, path] of trails.all) {
      if (path.verts.length < 2) continue;
      const [stroke, glow] = COLOR_HEX[path.color];

      ctx.beginPath();
      ctx.moveTo(path.verts[0]!.x, path.verts[0]!.y);
      for (let i = 1; i < path.verts.length - 1; i++) {
        ctx.lineTo(path.verts[i]!.x, path.verts[i]!.y);
      }
      const p = match.players[index];
      const last = path.verts[path.verts.length - 1]!;
      if (p && p.state !== PlayerState.Dead) {
        ctx.lineTo(
          p.bike.px + p.bike.w / 2 + (p.bike.x - p.bike.px) * alpha,
          p.bike.py + p.bike.h / 2 + (p.bike.y - p.bike.py) * alpha,
        );
      } else {
        ctx.lineTo(last.x, last.y);
      }

      ctx.shadowColor = glow;
      ctx.shadowBlur = 12;
      ctx.strokeStyle = glow;
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = stroke;
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawBonuses(bonuses: readonly Bonus[]): void {
    const { ctx } = this;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const b of bonuses) {
      const style = BONUS_STYLE[b.kind];
      ctx.shadowColor = style.fill;
      ctx.shadowBlur = 14;
      ctx.fillStyle = "rgba(2, 4, 10, 0.85)";
      ctx.fillRect(b.x, b.y, b.w, b.h);
      ctx.shadowBlur = 6;
      ctx.strokeStyle = style.fill;
      ctx.lineWidth = 2;
      ctx.strokeRect(b.x + 1, b.y + 1, b.w - 2, b.h - 2);
      ctx.shadowBlur = 0;
      ctx.fillStyle = style.fill;
      ctx.font = "700 12px Orbitron, sans-serif";
      ctx.fillText(style.label, b.x + b.w / 2, b.y + b.h / 2 + 1);
    }
    ctx.restore();
  }

  private drawBikes(match: Match, alpha: number): void {
    const { ctx } = this;
    const cfg = match.config.bonus;
    ctx.save();
    ctx.shadowBlur = 14;
    for (const p of match.players) {
      if (p.state === PlayerState.Dead) continue;
      const b = p.bike;
      const x = Math.round(b.px + (b.x - b.px) * alpha);
      const y = Math.round(b.py + (b.y - b.py) * alpha);
      const [stroke, glow] = COLOR_HEX[p.color];

      const jumping = p.effect === BonusKind.Jump && p.bonusTimer !== cfg.jumpDuration;
      const boosting = p.effect === BonusKind.Boost && p.bonusTimer !== cfg.boostDuration;
      const halo =
        p.state === PlayerState.Doubt || jumping ? "#ffffff" : boosting ? "#33ccff" : null;

      if (halo) {
        ctx.fillStyle = halo;
        ctx.shadowColor = halo;
        ctx.fillRect(x - 3, y - 3, b.w + 6, b.h + 6);
      }
      ctx.shadowColor = glow;
      ctx.fillStyle = stroke;
      ctx.fillRect(x - 1, y - 1, b.w + 2, b.h + 2);
    }
    ctx.restore();
  }

  private drawGrid(): void {
    const { ctx } = this;
    const g = this.grid;
    ctx.save();
    ctx.strokeStyle = "#0d2740";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = g.x; x <= g.x + g.width; x += 50) {
      ctx.moveTo(x, g.y);
      ctx.lineTo(x, g.y + g.height);
    }
    for (let y = g.y; y <= g.y + g.height; y += 50) {
      ctx.moveTo(g.x, y);
      ctx.lineTo(g.x + g.width, y);
    }
    ctx.stroke();
    ctx.strokeStyle = "#1f6fb0";
    ctx.shadowColor = "#0af";
    ctx.shadowBlur = 10;
    ctx.strokeRect(g.x, g.y, g.width, g.height);
    ctx.restore();
  }

  private drawPanel(match: Match | null): void {
    const { ctx } = this;
    const x0 = this.arenaWidth + 16;
    const w = PANEL_WIDTH - 32;
    ctx.save();
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";

    ctx.fillStyle = "#7fb2d8";
    ctx.font = "700 18px Orbitron, sans-serif";
    ctx.fillText("THE GRID", x0, 28);

    if (match) {
      const gap = match.players.length > 5 ? 32 : 42;
      let y = 64;
      for (const p of match.players) {
        this.drawPanelRow(match, p, x0, w, y);
        y += gap;
      }

      ctx.fillStyle = "#4c7aa0";
      ctx.font = "500 12px Orbitron, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(`Round ${match.round}`, x0, y + 6);
      if (match.lastEvent) {
        ctx.fillStyle = "#7fb2d8";
        wrapText(ctx, match.lastEvent.text, x0, y + 26, w, 15);
      }
    }
    ctx.restore();
  }

  private drawPanelRow(match: Match, p: Player, x0: number, w: number, y: number): void {
    const { ctx } = this;
    const [stroke] = COLOR_HEX[p.color];
    const dead = p.state === PlayerState.Dead;

    ctx.fillStyle = dead ? "#33475a" : stroke;
    ctx.fillRect(x0, y - 6, 12, 12);

    ctx.fillStyle = "#cfe8ff";
    ctx.font = "500 13px Orbitron, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`P${p.index + 1}`, x0 + 20, y);

    if (p.effect !== BonusKind.None) {
      ctx.fillStyle = p.effect === BonusKind.Boost ? "#33ccff" : "#7bed4b";
      ctx.font = "700 10px Orbitron, sans-serif";
      ctx.fillText(p.effect === BonusKind.Boost ? "BOOST" : "JUMP", x0 + 52, y);
    }

    ctx.fillStyle = "#cfe8ff";
    ctx.font = "500 14px Orbitron, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`${match.scores[p.index] ?? 0}`, x0 + w, y);
    ctx.textAlign = "left";

    const barY = y + 10;
    ctx.fillStyle = "#0d2740";
    ctx.fillRect(x0, barY, w, 3);
    ctx.fillStyle = stroke;
    const frac = Math.min(1, (match.scores[p.index] ?? 0) / match.config.victoryScore);
    ctx.fillRect(x0, barY, w * frac, 3);
  }

  private drawCountdown(seconds: number): void {
    const { ctx } = this;
    const label = seconds > 0 ? String(Math.ceil(seconds)) : "GO";
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "700 96px Orbitron, sans-serif";
    ctx.fillStyle = "#cfe8ff";
    ctx.shadowColor = "#0af";
    ctx.shadowBlur = 24;
    ctx.fillText(label, this.arenaWidth / 2, this.height / 2);
    ctx.restore();
  }
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = word;
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, cy);
}
